import { format } from 'date-fns';
import {
  createSubject,
  updateAccountAddress,
  updateSubject,
} from '../../../api/user';
import {
  composeValidators,
  parseNumber,
  validateEmail,
  validateInn,
  validateMinLength,
  validateName,
  validatePassport,
  validateRequired,
  validateSnils,
} from '../../../helpers/ValidatorHelper';
import { AccountSubjectResponse } from '../../../structures/response/AccountSubjectResponse';
import { AccountSubjectType } from '../../../enum/AccountSubjectType';
import { DadataAddressResponse } from '../../../structures/response/dadata/DadataAddressResponse';
import {
  AccountAddressResponse,
  AccountAddressTypeEnum,
} from '../../../structures/response/AccountAddressResponse';

export interface AgentPersonFormValues {
  fio: string;
  email: string;
  inn: string;
  snils: string;
  passport: string;
  subdivisionCode: string;
  passportIssuedBy: string;
  passportIssueDate: string;
  bDate: string;
  bPlace: string;
}

export const responseToForm = (
  response: AccountSubjectResponse
): Partial<AgentPersonFormValues> => {
  const {
    inn,
    bPlace,
    bdate,
    passportIssueDate,
    passportIssuedBy,
    snils,
    subdivisionCode,
    fname,
    mname,
    lname,
    passportSeries,
    passportNumber,
  } = response || {};
  return {
    inn,
    bPlace,
    bDate: bdate || '',
    passportIssueDate,
    passport: [passportSeries, passportNumber].filter(Boolean).join(' '),
    passportIssuedBy,
    snils,
    subdivisionCode,
    fio: [lname, fname, mname].filter(Boolean).join(' '),
  };
};

const formToResponse = (
  values: AgentPersonFormValues
): Partial<AccountSubjectResponse> => {
  const {
    inn,
    bPlace,
    bDate,
    passportIssueDate,
    passport,
    passportIssuedBy,
    snils,
    subdivisionCode,
    fio,
  } = values;

  const fioParts = fio.trim().split(' ');
  const passportParsed = parseNumber(passport);

  return {
    inn,
    bPlace,
    snils,
    subdivisionCode,
    passportIssuedBy,
    lname: fioParts[0],
    fname: fioParts[1],
    mname: fioParts[2],
    bdate: format(new Date(bDate), 'yyyy-MM-dd'),
    passportIssueDate: format(new Date(passportIssueDate), 'yyyy-MM-dd'),
    passportSeries: passportParsed.substring(0, 4),
    passportNumber: passportParsed.substring(4, 10),
  };
};

const dadataAddressToAddressRequest = (
  dadataResponse: DadataAddressResponse
) => ({
  regionCode: dadataResponse.region_kladr_id
    ? dadataResponse.region_kladr_id.substr(0, 2)
    : '',
  regionName: `${dadataResponse.region} ${dadataResponse.region_type_full}`,
  city: dadataResponse.city_with_type ? dadataResponse.city_with_type : '',
  cityId: dadataResponse.city_fias_id ? dadataResponse.city_fias_id : '',
  street: dadataResponse.street_with_type
    ? dadataResponse.street_with_type
    : '',
  streetId: dadataResponse.street_fias_id ? dadataResponse.street_fias_id : '',
  house: dadataResponse.house ? dadataResponse.house : '',
  room: dadataResponse.flat ? dadataResponse.flat : '',
  settlement: dadataResponse.settlement_with_type
    ? dadataResponse.settlement_with_type
    : '',
  settlementId: dadataResponse.settlement_fias_id
    ? dadataResponse.settlement_fias_id
    : '',
  area: dadataResponse.area_with_type ? dadataResponse.area_with_type : '',
  areaId: dadataResponse.area_fias_id ? dadataResponse.area_fias_id : '',
  postalCode: dadataResponse.postal_code
    ? dadataResponse.postal_code.toString()
    : '',
  block: dadataResponse?.block || '',
  blockType: dadataResponse?.block_type || '',
  blockTypeFull: dadataResponse?.block_type_full || '',
  houseType: dadataResponse?.house_type || '',
  houseTypeFull: dadataResponse?.house_type_full || '',
  flatType: dadataResponse?.flat_type || '',
  flatTypeFull: dadataResponse?.flat_type_full || '',
});

export const submit = async (
  values: AgentPersonFormValues,
  initialSubject: AccountSubjectResponse
) => {
  const updateSubjectCallback = initialSubject?.id
    ? updateSubject
    : createSubject;

  const subject: Partial<AccountSubjectResponse> = {
    ...initialSubject,
    type: AccountSubjectType.PERSON,
    ...formToResponse(values),
  };

  return updateSubjectCallback(subject);
};

export const submitAddress = (v: DadataAddressResponse) => {
  const request = dadataAddressToAddressRequest(v);
  return updateAccountAddress({
    ...request,
    type: AccountAddressTypeEnum.TYPE_LEGAL,
  });
};

export const fioValidator = composeValidators(validateRequired, validateName);

export const emailValidator = composeValidators(
  validateRequired,
  validateEmail
);

export const innValidator = composeValidators(validateRequired, validateInn);

export const snilsValidator = composeValidators(
  validateRequired,
  validateSnils
);

export const passportValidator = composeValidators(
  validateRequired,
  validatePassport
);

export const subdivisionCodeValidator = composeValidators(
  validateRequired,
  validateMinLength(7)
);

export const addressResponseToString = (address: AccountAddressResponse) => {
  const {
    regionName,
    area,
    settlement,
    city,
    street,
    houseType,
    house,
    blockType,
    block,
    flatType,
    room,
  } = address || {};

  const parts = [regionName, area, settlement, city, street];

  if (house) {
    parts.push(houseType ? `${houseType} ${house}` : house);
  }

  if (block) {
    parts.push(blockType ? `${blockType} ${block}` : block);
  }

  if (room) {
    parts.push(flatType ? `${flatType} ${room}` : room);
  }

  return parts.filter((s: string) => Boolean(s)).join(', ');
};
